import React, { useMemo } from 'react';
import { Box, Drawer, IconButton, Toolbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import type { Node } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSmsPageDesign, smsPageTokens } from '../../theme/smsPageDesign';
import {
  AbTestNodeInspectorForm,
  ChannelNodeInspectorForm,
  ConditionNodeInspectorForm,
  DelayNodeInspectorForm,
  NoteNodeInspectorForm,
  StartNodeInspectorForm,
} from './FlowNodes';
import { normalizeChannelNodeData } from './mailingTypes';

function inspectorTitle(node: Node, t: TFunction): string {
  switch (node.type) {
    case 'mailingStart':
      return t('flowNodes.startTitle');
    case 'mailingChannel':
      return t(`channels.${normalizeChannelNodeData(node.data).channel}`);
    case 'mailingDelay':
      return t('flowNodes.delayCardTitle');
    case 'mailingCondition':
      return t('flowNodes.conditionTitle');
    case 'mailingAbTest':
      return t('flowNodes.abTitle');
    case 'mailingNote':
      return t('flowEditor.paletteNote');
    default:
      return t('flowEditor.inspectorTitle');
  }
}

export type MailingFlowInspectorPanelProps = {
  node: Node | null;
  open: boolean;
  onClose: () => void;
  /** Только просмотр полей блока (цепочка зафиксирована запуском кампании). */
  readOnly?: boolean;
};

export function MailingFlowInspectorPanel({
  node,
  open,
  onClose,
  readOnly = false,
}: MailingFlowInspectorPanelProps) {
  const { t } = useTranslation();
  const { isSmsLight } = useSmsPageDesign();
  const pageTokens = smsPageTokens(isSmsLight);
  const title = useMemo(() => (node ? inspectorTitle(node, t) : ''), [node, t]);

  const body =
    node &&
    (() => {
      switch (node.type) {
        case 'mailingStart':
          return <StartNodeInspectorForm id={node.id} data={node.data} />;
        case 'mailingChannel':
          return <ChannelNodeInspectorForm id={node.id} data={node.data} />;
        case 'mailingDelay':
          return <DelayNodeInspectorForm id={node.id} data={node.data} />;
        case 'mailingCondition':
          return <ConditionNodeInspectorForm id={node.id} data={node.data} />;
        case 'mailingAbTest':
          return <AbTestNodeInspectorForm id={node.id} data={node.data} />;
        case 'mailingNote':
          return <NoteNodeInspectorForm id={node.id} data={node.data} />;
        default:
          return null;
      }
    })();

  return (
    <Drawer
      anchor="right"
      open={open && Boolean(node)}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100%',
          zIndex: 1300,
          borderLeft: `1px solid ${pageTokens.outlineBorder}`,
          bgcolor: pageTokens.white,
        },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: 1.5,
          minHeight: 48,
          borderBottom: 1,
          borderColor: pageTokens.divider,
          bgcolor: pageTokens.card,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {node?.type === 'mailingDelay' ? (
            <ScheduleIcon sx={{ fontSize: 22, color: 'secondary.main', flexShrink: 0 }} />
          ) : null}
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
        </Box>
        <IconButton edge="end" aria-label={t('flowEditor.closeInspector')} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <Box
        sx={{
          p: 1.5,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 56px)',
          ...(readOnly ? { pointerEvents: 'none', opacity: 0.88, userSelect: 'none' as const } : {}),
        }}
      >
        {body}
      </Box>
    </Drawer>
  );
}
